import * as turf from "@turf/turf";
import shp from 'shpjs';
import { computeMaxAssetRiskFrom } from "./calculateResult";

const VEGETATION_PRIORITY = ['other', 'grass', 'crop', 'scrub', 'wood'];

const DANGER_MATRIX = {
  low: { other: 'low', grass: 'low', crop: 'low', scrub: 'low', wood: 'low' },
  moderate: { other: 'elevated', grass: 'elevated', crop: 'elevated', scrub: 'high', wood: 'high' },
  high: { other: 'elevated', grass: 'high', crop: 'high', scrub: 'high', wood: 'high' },
};

const RISK_PALETTE = {
  low: '#66CDAA',
  elevated: '#F2C94C',
  high: '#e33121',
};

const RISK_OUTLINE = {
  low: '#3B9E7A',
  elevated: '#B08A1A',
  high: '#C7543D',
};

const DEFAULT_BY_VEG = {
  other: 'low',
  grass: 'elevated',
  crop: 'elevated',
  scrub: 'high',
  wood: 'high',
};

const MAX_DN_PROXIMITY_KM = 100;
let __fireDangerFC = null;

const OVERLAY_IDS = {
  src: 'asset-paint-src',
  fill: 'asset-paint-fill',
  line: 'asset-paint-line',
};

const __overlayReady = new WeakMap();
const __overlayPendingRefresh = new WeakMap();

async function ensureFireDangerFC() {
  if (__fireDangerFC) return __fireDangerFC;
  const shpData = await shp("/data/scaled_poly")

  if (!shpData)
    return null;

  const sortedFeatures = shpData.features.sort((a, b) => {
    const dnA = Number(a.properties.DN);
    const dnB = Number(b.properties.DN);
    return dnA - dnB;
  });
  __fireDangerFC = { type: 'FeatureCollection', features: sortedFeatures };

  return __fireDangerFC;
}

function bandFromDN(dn) {
  const n = Number(dn);
  if (!Number.isFinite(n)) return 'low';
  if (n < 20) return 'low';
  if (n < 60) return 'moderate';
  return 'high';
}

function dnForAssetPolygonWithNearest(assetPoly, fireFC) {
  let dnIntersect = null;

  fireFC.features.forEach(f => {
    const isIntersectFeature = turf.intersect(assetPoly, f);

    if (isIntersectFeature) {
      dnIntersect = Number(f.properties?.DN);

      return;
    }
  });

  if (dnIntersect)
    return { dn: dnIntersect, dnBand: bandFromDN(dnIntersect), mode: 'intersect', distKm: 0 };
  else {
    const c = turf.centroid(assetPoly);
    let best = { distKm: Infinity, dn: undefined };

    fireFC.features.forEach(f => {
      const line = turf.polygonToLine(f);
      const snap = turf.nearestPointOnLine(line, c);
      const dKm = turf.distance(c, snap, { units: 'kilometers' });
      const dnVal = Number(f.properties?.DN);
      if (dKm < best.distKm && Number.isFinite(dnVal)) {
        best = { distKm: dKm, dn: dnVal };
      }
    });

    if (best.distKm <= MAX_DN_PROXIMITY_KM && Number.isFinite(best.dn)) {
      return { dn: best.dn, dnBand: bandFromDN(best.dn), mode: 'nearest', distKm: best.distKm };
    }

    return { dn: undefined, dnBand: undefined, mode: 'none', distKm: Infinity };
  }
}

export async function tagAndPaintAssetRisk(map, draw, poly, setRiskInfo) {
  if (!poly?.id) return;

  await (async () => {
    const vegClass = pickVegClassForPolygon(map, poly);

    const fireFC = await ensureFireDangerFC();
    const res = dnForAssetPolygonWithNearest(poly, fireFC);

    const useDN = res.mode === 'inside' || res.mode === 'intersect' ||
      (res.mode === 'nearest' && res.distKm <= MAX_DN_PROXIMITY_KM);

    const dnBand = res.dnBand;
    const finalRisk = useDN
      ? (DANGER_MATRIX[dnBand]?.[vegClass] || 'low')
      : DEFAULT_BY_VEG[vegClass] || 'low';

    setAssetRiskProps(draw, poly.id, vegClass, finalRisk, res.dn, dnBand);
    draw.setFeatureProperty(poly.id, 'riskDNSource', res.mode);
    draw.setFeatureProperty(poly.id, 'riskDNNearestKm', isFinite(res.distKm) ? Number(res.distKm.toFixed(3)) : null);
    draw.setFeatureProperty(poly.id, 'riskDNUsed', useDN);
    draw.setFeatureProperty(poly.id, 'riskFinal', finalRisk);

    refreshAssetPaintOverlay(map, draw);
    const updatedColors = vegColorsByClass(finalRisk);
    repaintExistingBuffers(map, poly.id, updatedColors);

    const info = computeMaxAssetRiskFrom(draw);
    setRiskInfo(info);
  })().catch(err => console.error('[asset-risk] compute failed:', err));;
}

function setAssetRiskProps(draw, featureId, vegClass, finalRisk) {
  const colors = vegColorsByClass(finalRisk);

  draw.setFeatureProperty(featureId, 'vegClass', vegClass);
  draw.setFeatureProperty(featureId, 'riskClass', finalRisk);
  draw.setFeatureProperty(featureId, 'riskFillColor', colors.fill);
  draw.setFeatureProperty(featureId, 'riskLineColor', colors.stroke);
  draw.setFeatureProperty(featureId, 'riskBufferFill', colors.bufFill);
  draw.setFeatureProperty(featureId, 'riskPolyFill', colors.polyFill);
  draw.setFeatureProperty(featureId, 'riskBufferOutline', colors.bufOutline);
}

export const vegColorsByClass = (finalRisk = 'low') => {
  const base = RISK_PALETTE[finalRisk] || RISK_PALETTE.low;
  const stroke = RISK_OUTLINE[finalRisk] || RISK_OUTLINE.low;

  return {
    fill: base,
    stroke,
    bufFill: rgba(base, 0.25),
    polyFill: rgba(base, 0.4),
    bufOutline: rgba(stroke, 0.8),
  };
};

function rgba(hex, a) {
  const h = hex.replace('#', '');
  const v = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  const r = (v >> 16) & 255, g = (v >> 8) & 255, b = v & 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function repaintExistingBuffers(map, polyId, colors) {
  if (!map) return;

  (map.getStyle()?.layers || [])
    .map(l => l.id)
    .filter(id => id.startsWith(`buffer-${polyId}-`) || id.startsWith(`buffer-outline-${polyId}-`))
    .forEach(id => {
      const isOutline = id.startsWith(`buffer-outline-${polyId}-`);
      if (map.getLayer(id)) {
        map.setPaintProperty(id, isOutline ? 'line-color' : 'fill-color',
          isOutline ? colors.bufOutline : colors.bufFill
        );
        if (!isOutline) {
          map.setPaintProperty(id, 'fill-outline-color', colors.bufOutline);
        }
      }
    });
}

export function pickVegClassForPolygon(map, polyFeature) {
  if (!map || !polyFeature) return 'other';

  const landcoverLayer = map.getLayer('landcover-2');
  if (!landcoverLayer) return 'other';

  const features = map.querySourceFeatures(landcoverLayer.source, {
    sourceLayer: 'landcover'
  });
  if (!features.length) return 'other';

  const intersectClasses = new Set();
  features.forEach(f => {
    if (turf.booleanIntersects(polyFeature, f)) {
      intersectClasses.add(f.properties.class);
    }
  });

  let bestClass = 'other';
  intersectClasses.forEach(cls => {
    if (VEGETATION_PRIORITY.includes(cls)) {
      if (VEGETATION_PRIORITY.indexOf(cls) > VEGETATION_PRIORITY.indexOf(bestClass)) {
        bestClass = cls;
      }
    }
  });

  return bestClass;
}

export function ensureAssetPaintOverlay(map) {
  if (!map) return false;

  const create = () => {
    try {
      if (!map.getSource(OVERLAY_IDS.src)) {
        map.addSource(OVERLAY_IDS.src, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        });
      }

      if (!map.getLayer(OVERLAY_IDS.fill)) {
        map.addLayer({
          id: OVERLAY_IDS.fill,
          type: 'fill',
          source: OVERLAY_IDS.src,
          paint: {
            'fill-color': ['coalesce', ['get', 'polyFill'], ['get', 'fill'], '#268fff'],
            'fill-opacity': 0.7
          }
        });
      }

      if (!map.getLayer(OVERLAY_IDS.line)) {
        map.addLayer({
          id: OVERLAY_IDS.line,
          type: 'line',
          source: OVERLAY_IDS.src,
          paint: {
            'line-color': ['coalesce', ['get', 'bufOutline'], ['get', 'stroke'], '#00137C'],
            'line-width': 1
          }
        });
      }

      if (map.getLayer(OVERLAY_IDS.line)) map.moveLayer(OVERLAY_IDS.line);
      if (map.getLayer(OVERLAY_IDS.fill)) map.moveLayer(OVERLAY_IDS.fill);

      __overlayReady.set(map, true);

      if (__overlayPendingRefresh.get(map)) {
        __overlayPendingRefresh.delete(map);
        requestAnimationFrame(() => refreshAssetPaintOverlay(map, window.draw || null));
      }
    } catch (err) {
      console.error('Error ensuring asset paint overlay:', err);
    }
  };

  if (!map.isStyleLoaded()) {
    __overlayReady.set(map, false);
    const onStyle = () => {
      map.off('styledata', onStyle);
      map.off('load', onStyle);
      create();
    };
    map.on('styledata', onStyle);
    map.on('load', onStyle);
    return false;
  }

  create();
  return true;
}

export function refreshAssetPaintOverlay(map, draw) {
  if (!map) return;

  if (!map.getSource(OVERLAY_IDS.src)) {
    __overlayPendingRefresh.set(map, true);
    ensureAssetPaintOverlay(map);
    return;
  }

  try {
    const all = draw?.getAll()?.features || [];
    const feats = all
      .filter(f =>
        (f?.properties?.assetContour || f?.properties?.isBuilding) &&
        (f?.geometry?.type === 'Polygon' || f?.geometry?.type === 'MultiPolygon') &&
        isValidPolygonGeom(f.geometry)
      )
      .map(f => ({
        type: 'Feature',
        id: f.id,
        geometry: f.geometry,
        properties: {
          bufFill: f.properties?.riskBufferFill ?? null,
          bufOutline: f.properties?.riskBufferOutline ?? null,
          fill: f.properties?.riskFillColor ?? null,
          stroke: f.properties?.riskLineColor ?? null,
          polyFill: f.properties?.riskPolyFill ?? null,
        }
      }));

    const fc = { type: 'FeatureCollection', features: feats };

    const src = map.getSource(OVERLAY_IDS.src);
    if (src) src.setData(fc);
  } catch (error) {
    console.error('Error refreshing asset paint overlay:', error);
  }
}

export function clearAssetPaintOverlay(map) {
  if (!map || !map.isStyleLoaded()) return;

  try {
    if (map.getLayer('asset-paint-line')) map.removeLayer('asset-paint-line');
    if (map.getLayer('asset-paint-fill')) map.removeLayer('asset-paint-fill');
    if (map.getSource('asset-paint-src')) map.removeSource('asset-paint-src');
  } catch (error) {
    console.error('Error clearing asset paint overlay:', error);
  }
}

export const isValidPolygonGeom = (geom) => {
  if (!geom) return false;
  const isNum = (n) => typeof n === 'number' && Number.isFinite(n);
  const isPos = (c) => Array.isArray(c) && c.length >= 2 && isNum(c[0]) && isNum(c[1]);
  const isRing = (ring) => Array.isArray(ring) && ring.length >= 4 && ring.every(isPos);

  if (geom.type === 'Polygon') {
    return Array.isArray(geom.coordinates) &&
      geom.coordinates.length > 0 &&
      isRing(geom.coordinates[0]);
  }

  if (geom.type === 'MultiPolygon') {
    return Array.isArray(geom.coordinates) &&
      geom.coordinates.some(poly =>
        Array.isArray(poly) &&
        poly.length > 0 &&
        isRing(poly[0])
      );
  }

  return false;
};